import { R } from '@/modules/api/actions'
import { AuthContext } from '@/modules/auth/authContext'
import {
    AdminClientSettings,
    UpdateClientInput,
} from '@storefronts/common-types'
import {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'

type SettingsContextType = {
    settings: AdminClientSettings | null
    setSettings: Dispatch<SetStateAction<AdminClientSettings | null>>
    refreshSettings: () => Promise<AdminClientSettings | null>
    updateSettings: (
        newSettings: Partial<AdminClientSettings>,
    ) => Promise<AdminClientSettings | null>
}

export const SettingsContext = createContext<SettingsContextType>({
    settings: null,
    setSettings: () => {},
    refreshSettings: async () => null,
    updateSettings: async () => null,
})

export const SettingsProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [settings, setSettings] = useState<AdminClientSettings | null>(null)
    const { client } = useContext(AuthContext)

    useEffect(() => {
        const fetchSettings = async () => {
            if (!client) return
            const resp = await R.getClientSettings(client.clientHash)
            if (resp.isError) {
                console.error(resp.message)
                return
            }
            setSettings(resp.result.settings)
        }
        fetchSettings()
    }, [client])

    const refreshSettings = useCallback(async () => {
        if (!client) return null

        const response = await R.getClientSettings(client.clientHash)
        if (!response.isError && response.result?.settings) {
            setSettings(response.result.settings)
            return response.result.settings
        }
        return null
    }, [client, setSettings])

    const updateSettings = useCallback(
        async (newSettings: Partial<AdminClientSettings>) => {
            const response = await R.updateClientSettings(newSettings)

            if (response.isError) {
                throw new Error('Failed to update settings')
            }

            if (response.result?.settings) {
                setSettings(response.result.settings)
                return response.result.settings
            }

            return null
        },
        [setSettings],
    )

    return (
        <SettingsContext.Provider
            value={{
                settings,
                setSettings,
                refreshSettings,
                updateSettings,
            }}
        >
            {children}
        </SettingsContext.Provider>
    )
}

export const useSettings = () => {
    const settingsContext = useContext(SettingsContext)
    const authContext = useContext(AuthContext)

    if (settingsContext === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider')
    }

    return {
        ...settingsContext,
        client: authContext.client,
        updateClient: async (clientData: UpdateClientInput) => {
            try {
                const response = await R.updateClient(clientData)
                authContext.setClient(response.client)
                return response.client
            } catch (error) {
                console.error('Failed to update client:', error)
                throw new Error('Failed to update client')
            }
        },
    }
}
